import api from './api';

export interface ContextOption {
  value: string;
  label: string;
  /** Section of the context picker: "Entities", "Questionnaires" or
   *  "Send Questionnaire". Sent by the server so both template screens group
   *  the same way without re-deriving it from the value prefix. */
  group?: string;
}

export interface EmailTemplate {
  id: number;
  tenant_id: number;
  context: string;
  name: string;
  internal_id?: string | null;
  subject: string;
  html_content: string;
  language: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplateCreate {
  context: string;
  name: string;
  internal_id?: string;
  subject: string;
  html_content: string;
  language?: string;
  is_active?: boolean;
}

export interface EmailTemplateUpdate {
  context?: string;
  name?: string;
  internal_id?: string;
  subject?: string;
  html_content?: string;
  language?: string;
  is_active?: boolean;
}

export interface AvailableVariable {
  name: string;
  description: string;
  example: string;
  prompt_only?: boolean;
}

export interface AvailableVariablesResponse {
  context: string;
  variables: AvailableVariable[];
}

class EmailTemplateService {
  async getTemplates(context?: string, isActive?: boolean): Promise<EmailTemplate[]> {
    const params = new URLSearchParams();
    if (context) params.append('context', context);
    if (isActive !== undefined) params.append('is_active', String(isActive));

    const response = await api.get(`/email-templates/?${params.toString()}`);
    return response.data.items;
  }

  async getTemplate(id: number): Promise<EmailTemplate> {
    const response = await api.get(`/email-templates/${id}`);
    return response.data;
  }

  async createTemplate(template: EmailTemplateCreate): Promise<EmailTemplate> {
    const response = await api.post('/email-templates/', template);
    return response.data;
  }

  async updateTemplate(id: number, template: EmailTemplateUpdate): Promise<EmailTemplate> {
    const response = await api.put(`/email-templates/${id}`, template);
    return response.data;
  }

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/email-templates/${id}`);
  }

  async getAvailableVariables(context: string): Promise<AvailableVariable[]> {
    const response = await api.get(`/email-templates/contexts/${context}/variables`);
    return response.data.variables;
  }

  async getContexts(): Promise<ContextOption[]> {
    const response = await api.get('/email-templates/contexts');
    return response.data.contexts;
  }
}

const emailTemplateService = new EmailTemplateService();
export default emailTemplateService;
